import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;
const DEFAULT_RESIZE_MIN_WIDTH = 320;
const DEFAULT_RESIZE_MAX_RATIO = 0.5;
const KEYBOARD_RESIZE_STEP = 0.01;

/**
 * A drawer that defaults to the inline-end/right side. An individual host may
 * opt into inline-start placement with `data-drawer-side="inline-start"`;
 * placement remains CSS-owned and does not change this lifecycle owner.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open and returns to
 * the trigger on close.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 * @property {HTMLElement} [resizeHandle] - Optional desktop resize handle for an opted-in drawer.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /** @type {number | null} */
  #resizePointerId = null;

  /** @type {number | null} */
  #resizeStartRatio = null;

  /** @type {number | null} */
  #resizeStartStoredRatio = null;

  /** @type {string} */
  #resizeStartInlineWidth = '';

  /** @type {number | null} */
  #resizeRatio = null;

  /** @type {number | null} */
  #pendingResizeRatio = null;

  /** @type {number | null} */
  #resizeAnimationFrame = null;

  /** @type {boolean} */
  #resizeDidMove = false;

  /** @type {number | null} */
  #viewportResizeFrame = null;

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);
    if (this.#isResizable) window.addEventListener('resize', this.#onViewportResize);
    this.#syncResizableWidthForViewport();
    this.#syncToggleTriggers();

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncResizableWidthForViewport();
    this.#syncToggleTriggers();
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    const wasOpen = this.isOpen;

    this.#finishResize({ persist: false });
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    window.removeEventListener('resize', this.#onViewportResize);
    if (this.#viewportResizeFrame !== null) cancelAnimationFrame(this.#viewportResizeFrame);
    this.#viewportResizeFrame = null;
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;

    // Section Rendering can remove an open drawer when the updated result set
    // no longer exposes that surface (for example, clearing the final useful
    // Collection filter). Reconcile the global squeeze/persistence owners even
    // though this detached host can no longer bubble a normal close event.
    if (wasOpen) {
      this.removeAttribute('open');
      this.#syncToggleTriggers();
      document.dispatchEvent(new DrawerCloseEvent());
      if (!this.#handoffFocusToRemainingDrawer()) this.#restoreFocus();
    } else {
      this.#previouslyFocused = null;
    }
  }

  /**
   * Restores focus to the original trigger, its live Section Rendering
   * replacement, or an instance-declared fallback when the trigger disappears.
   */
  #restoreFocus() {
    const trigger = this.#previouslyFocused;
    const drawerId = this.id;
    const fallbackSelector = this.getAttribute('data-removal-focus-target');
    this.#previouslyFocused = null;

    if (trigger && document.contains(trigger)) {
      trigger.focus();
      return;
    }

    // A Section Rendering morph may insert the replacement immediately after
    // disconnectedCallback, so resolve live targets at the next microtask.
    queueMicrotask(() => {
      const replacementTrigger = Array.from(document.querySelectorAll('[aria-controls]')).find(
        (element) =>
          element.getAttribute('aria-controls') === drawerId &&
          element instanceof HTMLElement &&
          element.getClientRects().length > 0
      );

      if (replacementTrigger instanceof HTMLElement) {
        replacementTrigger.focus();
        return;
      }

      if (!fallbackSelector) return;

      const fallback = Array.from(document.querySelectorAll(fallbackSelector)).find(
        (element) => element instanceof HTMLElement && element.getClientRects().length > 0
      );
      if (fallback instanceof HTMLElement) fallback.focus();
    });
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    this.#finishResize({ persist: true });
    this.#syncResizableWidthForViewport();
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);

    // Every open drawer receives this media-query event in DOM order, which
    // is unrelated to visual stack order. Reconcile once that synchronous
    // batch completes so the highest stack entry owns the top layer and focus.
    queueMicrotask(() => this.#reconcileTopmostAfterBreakpoint());
  };

  #reconcileTopmostAfterBreakpoint() {
    if (!this.isConnected || !this.isOpen || !this.#isTopmost()) return;

    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      const nestedDialog = this.#getOpenNestedDialog();
      const nestedActiveElement =
        nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      if (nestedDialog?.open) nestedDialog.close();
      if (panel.open) panel.close();
      lockScroll(panel);
      panel.showModal();
      if (nestedDialog) this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    }

    this.#claimFocusOwnership();
  }

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Transient animation hint used when Chat docks; it does not choose drawer placement.
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.setAttribute('open', '');
    this.#syncToggleTriggers();
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Transient animation hint; it does not choose drawer placement.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      if (nestedDialog?.open) nestedDialog.close();
      panel.close();
      panel.showModal();
      if (nestedDialog) this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.#claimFocusOwnership();

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Keeps opt-in trigger state aligned with the live drawer without changing
   * Cart/Header trigger ownership.
   */
  #syncToggleTriggers() {
    if (!this.hasAttribute('data-sync-trigger-expanded')) return;

    for (const trigger of document.querySelectorAll('[data-drawer-toggle-trigger][aria-controls]')) {
      if (trigger.getAttribute('aria-controls') !== this.id) continue;
      trigger.setAttribute('aria-expanded', String(this.isOpen));
    }
  }

  /**
   * Makes this visual top drawer the keyboard-focus owner while preserving an
   * already-open nested dialog.
   */
  #claimFocusOwnership() {
    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (!this.#modalQuery.matches) trapFocus(panel);

    if (nestedDialog?.open) {
      if (nestedActiveElement) {
        nestedActiveElement.focus();
        return;
      }

      const nestedCloseButton = nestedDialog.querySelector(
        '.dialog-close-button, .theme-drawer__close-button, button[command="close"]'
      );
      if (nestedCloseButton instanceof HTMLElement) {
        nestedCloseButton.focus();
        return;
      }
    }

    this.#focusInitialElement();
  }

  /**
   * When multiple drawers are open, closing one must not remove the remaining
   * top drawer's focus containment or send focus behind it.
   *
   * @returns {boolean} Whether focus ownership moved to another open drawer.
   */
  #handoffFocusToRemainingDrawer() {
    let topDrawer = null;
    let topOrder = -1;

    for (const drawer of document.querySelectorAll('theme-drawer[open]')) {
      if (drawer === this) continue;
      const order = Number(/** @type {HTMLElement} */ (drawer).style.getPropertyValue('--drawer-stack-order') || 0);
      if (order < topOrder) continue;
      topDrawer = drawer;
      topOrder = order;
    }

    if (!(topDrawer instanceof HTMLElement)) return false;

    const panel = topDrawer.querySelector(':scope > dialog[ref="panel"]');
    if (!(panel instanceof HTMLDialogElement) || !panel.open) return false;

    if (!this.#modalQuery.matches) trapFocus(panel);

    const closeButton = panel.querySelector('.theme-drawer__close-button');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
    } else {
      panel.focus();
    }

    this.#previouslyFocused = null;
    return true;
  }

  /** @returns {boolean} */
  get #isResizable() {
    return this.hasAttribute('data-resizable');
  }

  /** @returns {number} */
  #getViewportWidth() {
    return Math.max(document.documentElement.clientWidth, window.innerWidth || 0, 1);
  }

  /**
   * @returns {{ viewportWidth: number, minWidth: number, minRatio: number, maxRatio: number }}
   */
  #getResizeBounds() {
    const viewportWidth = this.#getViewportWidth();
    const configuredMinWidth = Number(this.getAttribute('data-resize-min-width'));
    const configuredMaxRatio = Number(this.getAttribute('data-resize-max-ratio'));
    const minWidth = Number.isFinite(configuredMinWidth) && configuredMinWidth > 0
      ? configuredMinWidth
      : DEFAULT_RESIZE_MIN_WIDTH;
    const maxRatio = Number.isFinite(configuredMaxRatio) && configuredMaxRatio > 0
      ? Math.min(configuredMaxRatio, 1)
      : DEFAULT_RESIZE_MAX_RATIO;

    return {
      viewportWidth,
      minWidth,
      minRatio: Math.min(minWidth / viewportWidth, maxRatio),
      maxRatio,
    };
  }

  /**
   * @param {number} ratio
   * @returns {number}
   */
  #clampResizeRatio(ratio) {
    const { minRatio, maxRatio } = this.#getResizeBounds();
    return Math.min(Math.max(ratio, minRatio), maxRatio);
  }

  /** @returns {number} */
  #getCurrentResizeRatio() {
    if (this.#resizeRatio !== null) return this.#clampResizeRatio(this.#resizeRatio);

    const renderedWidth = Number.parseFloat(getComputedStyle(this.refs.panel).width);
    const fallbackRatio = Number.isFinite(renderedWidth) ? renderedWidth / this.#getViewportWidth() : 0;
    return this.#clampResizeRatio(fallbackRatio || DEFAULT_RESIZE_MAX_RATIO);
  }

  /** @returns {number | null} */
  #readSavedResizeRatio() {
    const key = this.getAttribute('data-resize-storage-key');
    if (!key) return null;

    try {
      const rawValue = localStorage.getItem(key);
      if (rawValue === null) return null;

      const ratio = Number(rawValue);
      if (Number.isFinite(ratio) && ratio > 0) return ratio;

      localStorage.removeItem(key);
    } catch {
      // Storage can be unavailable in privacy-restricted contexts. The
      // inherited 30rem drawer width remains the safe fallback.
    }

    return null;
  }

  /**
   * @param {number} ratio
   */
  #persistResizeRatio(ratio) {
    const key = this.getAttribute('data-resize-storage-key');
    if (!key) return;

    try {
      const { maxRatio } = this.#getResizeBounds();
      const safeRatio = Math.min(Math.max(ratio, Number.EPSILON), maxRatio);
      localStorage.setItem(key, safeRatio.toFixed(6));
    } catch {
      // Resizing remains functional when storage is unavailable.
    }
  }

  /**
   * @param {number} ratio
   * @param {{ persist?: boolean }} [options]
   */
  #applyResizeRatio(ratio, { persist = false } = {}) {
    if (!this.#isResizable || this.#modalQuery.matches) return;

    const { minWidth, maxRatio } = this.#getResizeBounds();
    const preferredRatio = Math.min(Math.max(ratio, Number.EPSILON), maxRatio);
    const renderedRatio = this.#clampResizeRatio(preferredRatio);
    this.#resizeRatio = preferredRatio;
    this.style.setProperty(
      '--theme-drawer-width',
      `clamp(${minWidth}px, ${(preferredRatio * 100).toFixed(4)}vw, ${(maxRatio * 100).toFixed(4)}vw)`
    );
    this.#updateResizeHandle(renderedRatio);
    this.dispatchEvent(new CustomEvent('theme-drawer:resize', { bubbles: true }));

    if (persist) this.#persistResizeRatio(preferredRatio);
  }

  /**
   * @param {number} ratio
   */
  #updateResizeHandle(ratio) {
    const handle = this.refs.resizeHandle;
    if (!(handle instanceof HTMLElement)) return;

    if (this.#modalQuery.matches) {
      handle.tabIndex = -1;
      handle.setAttribute('aria-hidden', 'true');
      return;
    }

    const { minRatio, maxRatio } = this.#getResizeBounds();
    const percentage = Math.round(this.#clampResizeRatio(ratio) * 100);
    handle.tabIndex = 0;
    handle.removeAttribute('aria-hidden');
    handle.setAttribute('aria-valuemin', String(Math.round(minRatio * 100)));
    handle.setAttribute('aria-valuemax', String(Math.round(maxRatio * 100)));
    handle.setAttribute('aria-valuenow', String(percentage));
    handle.setAttribute('aria-valuetext', `${percentage}%`);
  }

  /**
   * Applies a saved desktop ratio, or restores the inherited default width.
   * Mobile never consumes or overwrites the desktop preference.
   */
  #syncResizableWidthForViewport() {
    if (!this.#isResizable) return;
    if (this.#resizePointerId !== null) return;

    if (this.#modalQuery.matches) {
      const hadCustomWidth = this.style.removeProperty('--theme-drawer-width') !== '';
      this.#resizeRatio = null;
      this.#updateResizeHandle(this.#clampResizeRatio(DEFAULT_RESIZE_MAX_RATIO));
      if (hadCustomWidth) this.dispatchEvent(new CustomEvent('theme-drawer:resize', { bubbles: true }));
      return;
    }

    const savedRatio = this.#readSavedResizeRatio();
    if (savedRatio !== null) {
      this.#applyResizeRatio(savedRatio);
      return;
    }

    const hadCustomWidth = this.style.removeProperty('--theme-drawer-width') !== '';
    this.#resizeRatio = null;
    this.#updateResizeHandle(this.#getCurrentResizeRatio());
    if (hadCustomWidth) this.dispatchEvent(new CustomEvent('theme-drawer:resize', { bubbles: true }));
  }

  #onViewportResize = () => {
    if (!this.#isResizable || this.#viewportResizeFrame !== null) return;

    this.#viewportResizeFrame = requestAnimationFrame(() => {
      this.#viewportResizeFrame = null;
      if (this.#modalQuery.matches) return;

      const renderedWidth = Number.parseFloat(getComputedStyle(this.refs.panel).width);
      if (!Number.isFinite(renderedWidth)) return;
      this.#updateResizeHandle(renderedWidth / this.#getViewportWidth());
    });
  };

  /** @returns {boolean} */
  #isPhysicalInlineStartLeft() {
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const isInlineStart = this.getAttribute('data-drawer-side') === 'inline-start';
    return isInlineStart ? !isRtl : isRtl;
  }

  /**
   * @param {number} clientX
   * @returns {number}
   */
  #getResizeRatioFromPointer(clientX) {
    const viewportWidth = this.#getViewportWidth();
    const width = this.#isPhysicalInlineStartLeft() ? clientX : viewportWidth - clientX;
    return this.#clampResizeRatio(width / viewportWidth);
  }

  /**
   * Starts a pointer resize session. The global listeners exist only until
   * pointerup/cancel/blur and all visual work is coalesced to one frame.
   *
   * @param {PointerEvent} event
   */
  startResize(event) {
    if (!this.#isResizable || this.#modalQuery.matches || !this.isOpen) return;
    if (this.#resizePointerId !== null) return;
    if (event.button !== 0 || event.isPrimary === false) return;

    const handle = event.target;
    if (!(handle instanceof HTMLElement)) return;

    event.preventDefault();
    this.#resizePointerId = event.pointerId;
    this.#resizeStartInlineWidth = this.style.getPropertyValue('--theme-drawer-width');
    this.#resizeStartStoredRatio = this.#resizeRatio;
    this.#resizeStartRatio = this.#getCurrentResizeRatio();
    this.#resizeDidMove = false;
    this.toggleAttribute('data-resizing', true);
    document.documentElement.toggleAttribute('data-drawer-resizing', true);

    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      // Window listeners below still keep the drag coherent.
    }

    window.addEventListener('pointermove', this.#onResizePointerMove, { passive: false });
    window.addEventListener('pointerup', this.#onResizePointerUp);
    window.addEventListener('pointercancel', this.#onResizePointerCancel);
    window.addEventListener('blur', this.#onResizeWindowBlur);
  }

  /** @param {PointerEvent} event */
  #onResizePointerMove = (event) => {
    if (event.pointerId !== this.#resizePointerId) return;
    event.preventDefault();
    this.#pendingResizeRatio = this.#getResizeRatioFromPointer(event.clientX);

    if (this.#resizeAnimationFrame !== null) return;
    this.#resizeAnimationFrame = requestAnimationFrame(() => {
      this.#resizeAnimationFrame = null;
      const ratio = this.#pendingResizeRatio;
      this.#pendingResizeRatio = null;
      if (ratio !== null) {
        this.#resizeDidMove = true;
        this.#applyResizeRatio(ratio);
      }
    });
  };

  /** @param {PointerEvent} event */
  #onResizePointerUp = (event) => {
    if (event.pointerId !== this.#resizePointerId) return;
    this.#finishResize({ persist: true });
  };

  /** @param {PointerEvent} event */
  #onResizePointerCancel = (event) => {
    if (event.pointerId !== this.#resizePointerId) return;
    this.#finishResize({ persist: false, restoreStart: true });
  };

  #onResizeWindowBlur = () => {
    this.#finishResize({ persist: true });
  };

  #flushPendingResize() {
    if (this.#resizeAnimationFrame !== null) {
      cancelAnimationFrame(this.#resizeAnimationFrame);
      this.#resizeAnimationFrame = null;
    }

    const ratio = this.#pendingResizeRatio;
    this.#pendingResizeRatio = null;
    if (ratio !== null) {
      this.#resizeDidMove = true;
      this.#applyResizeRatio(ratio);
    }
  }

  /**
   * @param {{ persist: boolean, restoreStart?: boolean }} options
   */
  #finishResize({ persist, restoreStart = false }) {
    if (this.#resizePointerId === null) return;

    const pointerId = this.#resizePointerId;
    const handle = this.refs.resizeHandle;
    this.#flushPendingResize();

    if (restoreStart && this.#resizeStartRatio !== null && !this.#modalQuery.matches) {
      if (this.#resizeStartInlineWidth) {
        this.style.setProperty('--theme-drawer-width', this.#resizeStartInlineWidth);
      } else {
        this.style.removeProperty('--theme-drawer-width');
      }
      this.#resizeRatio = this.#resizeStartStoredRatio;
      const renderedWidth = Number.parseFloat(getComputedStyle(this.refs.panel).width);
      const restoredRatio = Number.isFinite(renderedWidth)
        ? renderedWidth / this.#getViewportWidth()
        : this.#resizeStartRatio;
      this.#updateResizeHandle(restoredRatio);
      this.dispatchEvent(new CustomEvent('theme-drawer:resize', { bubbles: true }));
    } else if (persist && this.#resizeDidMove && this.#resizeRatio !== null) {
      this.#persistResizeRatio(this.#resizeRatio);
    }

    if (handle instanceof HTMLElement) {
      try {
        if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
      } catch {
        // The browser may already have released capture.
      }
    }

    window.removeEventListener('pointermove', this.#onResizePointerMove);
    window.removeEventListener('pointerup', this.#onResizePointerUp);
    window.removeEventListener('pointercancel', this.#onResizePointerCancel);
    window.removeEventListener('blur', this.#onResizeWindowBlur);
    this.removeAttribute('data-resizing');
    document.documentElement.removeAttribute('data-drawer-resizing');
    this.#resizePointerId = null;
    this.#resizeStartRatio = null;
    this.#resizeStartStoredRatio = null;
    this.#resizeStartInlineWidth = '';
    this.#resizeDidMove = false;
  }

  /**
   * Keyboard equivalent of pointer resizing.
   *
   * @param {KeyboardEvent} event
   */
  resizeByKeyboard(event) {
    if (!this.#isResizable || this.#modalQuery.matches || !this.isOpen) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const { minRatio, maxRatio } = this.#getResizeBounds();
    let ratio = this.#getCurrentResizeRatio();

    if (event.key === 'Home') {
      ratio = minRatio;
    } else if (event.key === 'End') {
      ratio = maxRatio;
    } else {
      const step = KEYBOARD_RESIZE_STEP * (event.shiftKey ? 5 : 1);
      const physicalDirection = event.key === 'ArrowRight' ? 1 : -1;
      ratio += (this.#isPhysicalInlineStartLeft() ? physicalDirection : -physicalDirection) * step;
    }

    this.#applyResizeRatio(ratio, { persist: true });
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    this.#finishResize({ persist: true });
    this.removeAttribute('open');
    this.#syncToggleTriggers();
    this.dispatchEvent(new DrawerCloseEvent());
    const handedOffBeforeAnimation = !this.#modalQuery.matches && this.#handoffFocusToRemainingDrawer();

    unlockScroll(panel);

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    if (!handedOffBeforeAnimation && !this.#handoffFocusToRemainingDrawer()) this.#restoreFocus();

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && (this.hasAttribute('data-toggle-closes-when-open') || this.#isTopmost())) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
